import { useEffect, useState } from 'react';
import { Fragment } from 'react';
import { apiFetchWithTimeout } from '../../services/apiUtils';
import { buildTransactionReportParams, defaultTransactionReportFilters, isCurrentTransactionReportRequest, transactionReportHeaders, type TransactionReportClientFilters } from '../../services/transactionReport';
import { FilterPanel, ReportActionButton, ReportFilterField, ReportHeader, ReportPagination, ReportState, resetTransactionReportPage, scheduleTransactionReportFilterApply, SearchableMultiSelectReportFilter, SearchableReportSelect, SortableHeaderButton } from './ReportUi';

type Contact = {
  role?: string;
  first_name?: string;
  last_name?: string;
  name?: string;
  contact_number?: string;
  mobile_number?: string;
  telephone_number?: string;
  phone?: string;
  email?: string;
  company?: string;
  firm?: string;
};
type StatusChange = { previous_status?: string | null; new_status: string; changed_at: string };
type AssociateSplit = { name?: string; role?: string; team?: string; market_center?: string };
type TransactionRow = {
  transaction_id: string;
  report_classification?: 'sale' | 'rental' | 'unknown' | 'conflicting';
  transaction_number: string;
  listing_number: string;
  address: string;
  list_date: string | null;
  transaction_date: string | null;
  status_change_date: string | null;
  registration_date: string | null;
  transaction_status: string;
  market_center_name: string;
  team_names: string;
  associate_names: string;
  list_price: number | null;
  sales_price: number | null;
  contract_gci_excl_vat: number | null;
  total_gci: number | null;
  net_comm: number | null;
  company_dollar: number | null;
  team_dollar: number | null;
  associate_dollar: number | null;
  production_royalties: number | null;
  growth_share: number | null;
  cap_remaining: number | null;
  associate_splits: AssociateSplit[];
  buyers: Contact[] | null;
  sellers: Contact[] | null;
  transfer_attorney: Contact | null;
  bond_attorney: Contact | null;
  bond_originator: Contact | null;
  transfer_attorney_firm: string | null;
  bond_attorney_firm: string | null;
  bond_originator_company: string | null;
  transaction_financing_type: string | null;
  transaction_financial_institution: string | null;
  bond_amount: number | null;
  status_history: StatusChange[];
};

type ReportResponse = { items: TransactionRow[]; page: number; page_size: number; total_items: number; total_pages: number; contacts_included: boolean; classification_exceptions?: { unknown: number; conflicting: number } };
type FilterOptions = {
  statuses: string[];
  financing_types: string[];
  market_centres: Array<{ value: string; label: string }>;
  attorneys: string[];
  originators: string[];
  region_available?: boolean;
};
export type TransactionsReportAuth = {
  token: string | null;
  activeContext: { id: string } | null;
  isRegionalAdmin: boolean;
  isOfficeAdmin: boolean;
};

function today(): string {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Africa/Johannesburg',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());
  const year = parts.find((part) => part.type === 'year')?.value ?? '0000';
  const month = parts.find((part) => part.type === 'month')?.value ?? '01';
  const day = parts.find((part) => part.type === 'day')?.value ?? '01';
  return `${year}-${month}-${day}`;
}

function defaultFilters(): TransactionReportClientFilters {
  return defaultTransactionReportFilters(today());
}

function money(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(Number(value))) return '';
  return new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR', maximumFractionDigits: 2 }).format(Number(value));
}

function date(value: string | null | undefined): string {
  return value ? value.slice(0, 10) : '';
}

function contactName(contact: Contact): string {
  return contact.name || [contact.first_name, contact.last_name].filter(Boolean).join(' ');
}

function recorded(value: string | null | undefined): string {
  return value?.trim() || 'Not recorded';
}

function hasValue(value: string | null | undefined): boolean {
  return Boolean(value?.trim());
}

function DetailSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="h-full min-w-0 w-full rounded border p-2.5" style={{ borderColor: 'var(--border-soft)', background: 'var(--surface)' }}>
      <h3 className="mb-2 border-b pb-1.5 text-xs font-semibold" style={{ borderColor: 'var(--border-soft)', color: 'var(--text-primary)' }}>{title}</h3>
      <div className="min-w-0 space-y-1.5">{children}</div>
    </section>
  );
}

function ContactDetailField({ label, value, kind = 'text' }: { label: string; value: string; kind?: 'text' | 'email' | 'phone' }) {
  const emailParts = kind === 'email' ? value.split(/([.@_+-])/g) : [];
  return (
    <div className="flex min-w-0 items-start gap-2 text-xs">
      <dt className="w-[4.75rem] shrink-0" style={{ color: 'var(--text-muted)' }}>{label}</dt>
      <dd className={`min-w-0 flex-1 ${kind === 'phone' ? 'whitespace-nowrap tabular-nums' : 'break-words'}`} style={{ color: 'var(--text-primary)', overflowWrap: kind === 'email' ? 'break-word' : 'normal' }}>
        {kind === 'email' ? emailParts.map((part, index) => <Fragment key={`${index}-${part}`}>{part}{/[.@_+-]/.test(part) ? <wbr /> : null}</Fragment>) : value}
      </dd>
    </div>
  );
}

function PartyContactList({ label, role, contacts, permitted }: {
  label: string;
  role: 'Buyer' | 'Seller';
  contacts: Contact[] | null;
  permitted: boolean;
}) {
  if (!permitted) {
    return <div className="min-w-0"><h4 className="mb-2 text-sm font-semibold">{label}</h4><p className="text-sm">Restricted</p></div>;
  }
  if (!contacts?.length) {
    return <div className="min-w-0"><h4 className="mb-2 text-sm font-semibold">{label}</h4><p className="text-sm">No information recorded</p></div>;
  }
  return (
    <div className="min-w-0">
      <h4 className="mb-2 text-sm font-semibold">{label}</h4>
      <div className="min-w-0 space-y-1.5">
        {contacts.map((contact, index) => {
          const fullName = contact.name || [contact.first_name, contact.last_name].filter(Boolean).join(' ');
          return (
            <article key={`${role}-${index}`} className="min-w-0 rounded border p-2" style={{ borderColor: 'var(--border-soft)' }}>
              <h5 className="mb-1 text-xs font-semibold">{contact.role || role}{contacts.length > 1 ? ` ${index + 1}` : ''}</h5>
              <dl className="min-w-0 space-y-1">
                {hasValue(fullName) ? <ContactDetailField label="Name" value={fullName} /> : null}
                {hasValue(contact.email) ? <ContactDetailField label="Email" value={contact.email ?? ''} kind="email" /> : null}
                {hasValue(contact.telephone_number || contact.contact_number || contact.phone) ? <ContactDetailField label="Telephone" value={contact.telephone_number || contact.contact_number || contact.phone || ''} kind="phone" /> : null}
                {hasValue(contact.company || contact.firm) ? <ContactDetailField label="Company" value={contact.company || contact.firm || ''} /> : null}
              </dl>
            </article>
          );
        })}
      </div>
    </div>
  );
}

export function TransactionReportExpandedDetails({ row, contactsIncluded }: { row: TransactionRow; contactsIncluded: boolean }) {
  return (
    <div className="grid min-w-0 grid-cols-1 items-stretch gap-2 xl:grid-cols-3">
      <DetailSection title="Buyers & Sellers">
        {!contactsIncluded ? <p className="text-xs">Restricted</p>
          : !row.buyers?.length && !row.sellers?.length ? <p className="text-xs">No information recorded</p>
            : <div className="grid min-w-0 grid-cols-1 gap-2">
              <PartyContactList label="Buyers" role="Buyer" contacts={row.buyers} permitted />
              <PartyContactList label="Sellers" role="Seller" contacts={row.sellers} permitted />
            </div>}
      </DetailSection>

      <DetailSection title="Associates">
        {row.associate_splits.length ? <div className="flex min-w-0 flex-wrap gap-1.5">
          {row.associate_splits.map((associate, index) => <article key={`${row.transaction_id}-associate-${index}`} className="min-w-0 rounded border px-2 py-1.5 text-xs" style={{ borderColor: 'var(--border-soft)' }}>
            <p className="truncate font-semibold">{associate.name || 'Not recorded'}</p>
            <p className="truncate" style={{ color: 'var(--text-muted)' }}>{associate.market_center || row.market_center_name || 'Market Centre not recorded'}{associate.team ? ` · ${associate.team}` : ''}</p>
          </article>)}
        </div> : <p className="text-xs">No information recorded</p>}
      </DetailSection>

      <DetailSection title="Status History">
        {row.status_history.length ? <div className="flex min-w-0 flex-wrap gap-1.5">
          {row.status_history.map((change, index) => <article key={`${row.transaction_id}-status-${index}`} className="flex min-w-0 flex-wrap items-center gap-1 rounded border px-2 py-1.5 text-xs" style={{ borderColor: 'var(--border-soft)' }}>
            <span className="whitespace-nowrap" style={{ color: 'var(--text-muted)' }}>{date(change.changed_at)}</span>
            <span className="truncate">{recorded(change.previous_status) === 'Not recorded' ? 'Start' : recorded(change.previous_status)}</span>
            <span aria-hidden="true">→</span>
            <span className="truncate font-semibold">{change.new_status || 'Not recorded'}</span>
          </article>)}
        </div> : <p className="text-xs">No information recorded</p>}
      </DetailSection>
    </div>
  );
}

function TransactionsReportView({ previewMode = false, auth }: { previewMode?: boolean; auth: TransactionsReportAuth }) {
  const { token, activeContext, isRegionalAdmin, isOfficeAdmin } = auth;
  const [draft, setDraft] = useState(defaultFilters);
  const [filters, setFilters] = useState(draft);
  const [data, setData] = useState<ReportResponse | null>(null);
  const [options, setOptions] = useState<FilterOptions>({ statuses: [], financing_types: [], market_centres: [], attorneys: [], originators: [], region_available: false });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [showSecondaryFilters, setShowSecondaryFilters] = useState(false);
  const [exporting, setExporting] = useState<'csv' | 'xlsx' | null>(null);
  const [exportError, setExportError] = useState('');
  const canRequestContacts = isRegionalAdmin || isOfficeAdmin;
  const secondaryFilterCount = [
    Boolean(draft.team.trim()),
    Boolean(draft.associate.trim()),
    draft.date_type !== 'transaction',
    Boolean(draft.financing_type),
    Boolean(draft.attorney_role),
  ].filter(Boolean).length;
  const headers = previewMode ? {} : transactionReportHeaders(token, activeContext?.id);
  const reportEndpoint = previewMode ? '/api/report-preview/transactions' : '/api/reports/transactions';
  const filterOptionsEndpoint = previewMode ? '/api/report-preview/filter-options' : '/api/reports/transactions/filter-options';

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    setLoading(true);
    setError('');
    setData(null);
    const params = buildTransactionReportParams(filters);
    void apiFetchWithTimeout(`${reportEndpoint}?${params.toString()}`, { headers, credentials: previewMode ? 'include' : 'same-origin', signal: controller.signal }, 60000)
      .then(async (response) => {
        if (!response.ok) throw new Error((await response.json().catch(() => null))?.error || 'Could not load Transactions Report.');
        return response.json() as Promise<ReportResponse>;
      })
      .then((result) => { if (isCurrentTransactionReportRequest(active, controller.signal)) setData(result); })
      .catch((failure: unknown) => {
        if (isCurrentTransactionReportRequest(active, controller.signal) && !(failure instanceof DOMException && failure.name === 'AbortError')) setError(failure instanceof Error ? failure.message : 'Could not load Transactions Report.');
      })
      .finally(() => { if (isCurrentTransactionReportRequest(active, controller.signal)) setLoading(false); });
    return () => { active = false; controller.abort(); };
  }, [filters, token, activeContext?.id, previewMode, reportEndpoint]);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    setOptions({ statuses: [], financing_types: [], market_centres: [], attorneys: [], originators: [], region_available: false });
    const optionFilters = { ...filters, page: 1 };
    const params = buildTransactionReportParams(optionFilters, false);
    void apiFetchWithTimeout(`${filterOptionsEndpoint}?${params.toString()}`, { headers, credentials: previewMode ? 'include' : 'same-origin', signal: controller.signal }, 60000)
      .then(async (response) => {
        if (!response.ok) throw new Error('Could not load report filter options.');
        return response.json() as Promise<FilterOptions>;
      })
      .then((result) => { if (isCurrentTransactionReportRequest(active, controller.signal)) setOptions(result); })
      .catch(() => undefined);
    return () => { active = false; controller.abort(); };
  }, [filters.date_from, filters.date_to, filters.date_type, filters.transaction_type, filters.attorney_role, token, activeContext?.id, previewMode, filterOptionsEndpoint]);

  useEffect(() => {
    if (JSON.stringify(draft) === JSON.stringify(filters)) return undefined;
    const timer = scheduleTransactionReportFilterApply(() => {
      const next = resetTransactionReportPage(draft);
      setExpandedId(null);
      setDraft(next);
      setFilters((current) => JSON.stringify(current) === JSON.stringify(next) ? current : next);
    });
    return () => clearTimeout(timer);
  }, [draft, filters]);

  function updateDraft<K extends keyof TransactionReportClientFilters>(key: K, value: TransactionReportClientFilters[K]): void {
    setDraft((current) => ({ ...current, [key]: value }));
  }

  function applyFilters(): void {
    const next = resetTransactionReportPage(draft);
    setExpandedId(null);
    setDraft(next);
    setFilters((current) => JSON.stringify(current) === JSON.stringify(next) ? current : next);
  }

  function clearFilters(): void {
    const empty = defaultFilters();
    setDraft(empty);
    setFilters((current) => JSON.stringify(current) === JSON.stringify(empty) ? current : empty);
    setExpandedId(null);
  }

  function sortBy(sortBy: string): void {
    const direction = filters.sort_by === sortBy && filters.sort_direction === 'asc' ? 'desc' : 'asc';
    const next = { ...filters, sort_by: sortBy, sort_direction: direction as 'asc' | 'desc', page: 1 };
    setDraft(next);
    setFilters(next);
  }

  async function download(format: 'csv' | 'xlsx'): Promise<void> {
    if (previewMode || exporting) return;
    setExportError('');
    setExporting(format);
    const params = buildTransactionReportParams(filters, false);
    try {
      const response = await apiFetchWithTimeout(`/api/reports/transactions/export.${format}?${params.toString()}`, { headers }, 120000);
      if (!response.ok) throw new Error((await response.json().catch(() => null))?.error || 'Could not export Transactions Report.');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `transactions-report-${new Date().toISOString().slice(0, 10)}.${format}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (failure) {
      setExportError(failure instanceof Error ? failure.message : 'Could not export Transactions Report.');
    } finally {
      setExporting(null);
    }
  }

  const inputClass = 'min-w-0 w-full rounded-md border px-2.5 py-2 text-sm';
  const inputStyle = { borderColor: 'var(--border-soft)', color: 'var(--text-primary)', background: 'var(--surface)' };
  const reportFilterGridClass = 'grid w-full min-w-0 grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-3';
  const contactsIncluded = previewMode ? (data?.contacts_included ?? false) : canRequestContacts && (data?.contacts_included ?? false);

  return (
    <div className="space-y-3">
      <ReportHeader
        title="Transactions Report"
        subtitle="Transaction, hierarchy, financial, and party details"
      />

      <FilterPanel>
        <form onSubmit={(event) => { event.preventDefault(); applyFilters(); }}>
        <div className={reportFilterGridClass}>
          <ReportFilterField label="Date From">
            <input className={`${inputClass} box-border h-10`} style={inputStyle} type="date" value={draft.date_from} onChange={(event) => updateDraft('date_from', event.target.value)} />
          </ReportFilterField>
          <ReportFilterField label="Date To">
            <input className={`${inputClass} box-border h-10`} style={inputStyle} type="date" value={draft.date_to} onChange={(event) => updateDraft('date_to', event.target.value)} />
          </ReportFilterField>
          <ReportFilterField label="Transaction Type">
            <select aria-label="Transaction Type" className={`${inputClass} box-border h-10`} style={inputStyle} value={draft.transaction_type} onChange={(event) => updateDraft('transaction_type', event.target.value as TransactionReportClientFilters['transaction_type'])}>
              <option value="sales">Sales</option><option value="rentals">Rentals</option><option value="all">All transactions</option>
            </select>
          </ReportFilterField>
          <SearchableMultiSelectReportFilter
            label="Status"
            values={draft.status}
            options={options.statuses.map((status) => ({ value: status, label: status }))}
            placeholder="All statuses"
            onChange={(value) => updateDraft('status', value)}
            onApply={applyFilters}
            allowCustomValues={false}
            onApplySelection={(status) => {
              const next = resetTransactionReportPage({ ...draft, status });
              setDraft(next);
              setFilters(next);
              setExpandedId(null);
            }}
          />
          <SearchableMultiSelectReportFilter label="Market Centre" values={draft.market_center} options={options.market_centres} onChange={(value) => updateDraft('market_center', value)} onApply={applyFilters} placeholder="Search Market Centre" />
          <ReportFilterField label="Transaction / Property Search">
            <input className={`${inputClass} box-border h-10`} style={inputStyle} value={draft.search} onChange={(event) => updateDraft('search', event.target.value)} placeholder="TH number, listing or address" />
          </ReportFilterField>
          <SearchableMultiSelectReportFilter label="Attorney" values={draft.attorney} options={options.attorneys.map((value) => ({ value, label: value }))} onChange={(value) => updateDraft('attorney', value)} onApply={applyFilters} placeholder="Search name or firm" disabled={!canRequestContacts} />
          <SearchableMultiSelectReportFilter label="Bond Originator" values={draft.originator} options={options.originators.map((value) => ({ value, label: value }))} onChange={(value) => updateDraft('originator', value)} onApply={applyFilters} placeholder="Search person or company" disabled={!canRequestContacts} />
        </div>
        <button type="button" className="mt-2 text-xs font-semibold underline-offset-2 hover:underline" style={{ color: 'var(--brand)' }} aria-expanded={showSecondaryFilters} onClick={() => setShowSecondaryFilters((value) => !value)}>
          {showSecondaryFilters ? 'Fewer filters' : 'More filters'}{secondaryFilterCount ? ` (${secondaryFilterCount})` : ''}
        </button>
        {showSecondaryFilters ? <div className={`mt-2 ${reportFilterGridClass}`}>
          <ReportFilterField label="Team">
            <input className={`${inputClass} box-border h-10`} style={inputStyle} value={draft.team} onChange={(event) => updateDraft('team', event.target.value)} placeholder="Team name or ID" />
          </ReportFilterField>
          <ReportFilterField label="Associate">
            <input className={`${inputClass} box-border h-10`} style={inputStyle} value={draft.associate} onChange={(event) => updateDraft('associate', event.target.value)} placeholder="Associate name or ID" />
          </ReportFilterField>
          <ReportFilterField label="Date Type">
            <select className={`${inputClass} box-border h-10`} style={inputStyle} value={draft.date_type} onChange={(event) => updateDraft('date_type', event.target.value as TransactionReportClientFilters['date_type'])}>
              <option value="transaction">Transaction date</option><option value="status_change">Status change date</option>
            </select>
          </ReportFilterField>
          <SearchableReportSelect label="Financing type" value={draft.financing_type} options={options.financing_types.map((type) => ({ value: type, label: type }))} allLabel="All financing types" placeholder="Search financing type" onChange={(value) => updateDraft('financing_type', value)} onApply={applyFilters} />
          <ReportFilterField label="Attorney Role">
            <select className={`${inputClass} box-border h-10`} style={inputStyle} value={draft.attorney_role} onChange={(event) => updateDraft('attorney_role', event.target.value as TransactionReportClientFilters['attorney_role'])} disabled={!canRequestContacts}>
              <option value="">Transfer or bond</option><option value="transfer">Transferring attorney</option><option value="bond">Bond attorney</option>
            </select>
          </ReportFilterField>
        </div> : null}
        {!canRequestContacts && <p className="mt-3 text-xs" style={{ color: 'var(--text-muted)' }}>Party and professional filters are restricted for this role.</p>}
        <div className="mt-3 flex flex-wrap justify-end gap-2">
          <ReportActionButton label="Clear filters" onClick={clearFilters} />
          <button type="submit" disabled={!draft.date_from || !draft.date_to} className="inline-flex h-8 items-center rounded-md border px-4 text-xs font-semibold transition-opacity disabled:opacity-40" style={{ borderColor: 'var(--brand-soft)', color: '#fff', background: 'var(--brand)' }}>Apply filters</button>
        </div>
        </form>
      </FilterPanel>

      {error ? <ReportState type="error" message={error} /> : null}
      {loading ? <ReportState type="loading" message="Loading transactions..." /> : null}
      {!loading && !error && data?.total_items === 0 ? <ReportState type="empty" message="No transactions match these filters." /> : null}

      {!previewMode ? <div className="flex flex-wrap justify-end gap-2" aria-busy={exporting !== null}>
        <ReportActionButton label={exporting === 'csv' ? 'Exporting CSV...' : 'Export CSV'} onClick={() => void download('csv')} disabled={loading || !data || exporting !== null} />
        <ReportActionButton label={exporting === 'xlsx' ? 'Exporting Excel...' : 'Export Excel'} onClick={() => void download('xlsx')} disabled={loading || !data || exporting !== null} />
      </div> : null}
      {exportError ? <ReportState type="error" message={exportError} /> : null}

      {!loading && !error && data && data.total_items > 0 ? (
        <section className="surface-card overflow-hidden">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b px-4 py-3 text-sm" style={{ borderColor: 'var(--border-soft)' }}>
            <span>{data.total_items.toLocaleString()} transactions</span>
            {filters.transaction_type === 'all' && data.classification_exceptions ? <span className="text-xs" role="status">Classification exceptions: {data.classification_exceptions.unknown} unknown; {data.classification_exceptions.conflicting} conflicting</span> : null}
            <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Regional and Market Centre report scope is enforced by the server.</span>
          </div>
          <div className="overflow-x-auto" style={{ containerType: 'inline-size' }}>
            <table className="min-w-[1480px] w-full border-collapse text-left text-xs">
              <thead style={{ background: 'var(--surface-strong)', color: 'var(--text-muted)' }}>
                <tr>
                  <th className="px-3 py-2">Details</th>
                  <th className="px-3 py-2">TH # / Listing #</th>
                  <th className="px-3 py-2"><SortableHeaderButton label="Transaction date" active={filters.sort_by === 'transactionDate'} direction={filters.sort_direction} onClick={() => sortBy('transactionDate')} /></th>
                  <th className="px-3 py-2"><SortableHeaderButton label="Status" active={filters.sort_by === 'status'} direction={filters.sort_direction} onClick={() => sortBy('status')} /></th>
                  <th className="px-3 py-2"><SortableHeaderButton label="Status changed" active={filters.sort_by === 'statusChangeDate'} direction={filters.sort_direction} onClick={() => sortBy('statusChangeDate')} /></th>
                  <th className="px-3 py-2"><SortableHeaderButton label="Market Centre" active={filters.sort_by === 'marketCenter'} direction={filters.sort_direction} onClick={() => sortBy('marketCenter')} /></th>
                  <th className="px-3 py-2">Team(s)</th>
                  <th className="px-3 py-2">Associate(s)</th>
                  <th className="px-3 py-2"><SortableHeaderButton label="Transferring Attorney" active={filters.sort_by === 'transferAttorneyFirm'} direction={filters.sort_direction} onClick={() => sortBy('transferAttorneyFirm')} /></th>
                  <th className="px-3 py-2"><SortableHeaderButton label="Bond Attorney" active={filters.sort_by === 'bondAttorneyFirm'} direction={filters.sort_direction} onClick={() => sortBy('bondAttorneyFirm')} /></th>
                  <th className="px-3 py-2"><SortableHeaderButton label="Bond Originator" active={filters.sort_by === 'bondOriginatorCompany'} direction={filters.sort_direction} onClick={() => sortBy('bondOriginatorCompany')} /></th>
                  <th className="px-3 py-2">Financing type</th>
                  <th className="px-3 py-2 text-right"><SortableHeaderButton label="Sale Price" active={filters.sort_by === 'salesPrice'} direction={filters.sort_direction} onClick={() => sortBy('salesPrice')} /></th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((row) => (
                  <Fragment key={row.transaction_id}>
                    <tr className="border-t align-top" style={{ borderColor: 'var(--border-soft)', color: 'var(--text-primary)' }}>
                      <td className="px-3 py-2"><button className="font-semibold underline-offset-2 hover:underline" type="button" aria-expanded={expandedId === row.transaction_id} onClick={() => setExpandedId(expandedId === row.transaction_id ? null : row.transaction_id)}>{expandedId === row.transaction_id ? 'Hide' : 'View'}</button></td>
                      <td className="px-3 py-2 font-semibold">{row.transaction_number}<span className="mt-0.5 block font-normal" style={{ color: 'var(--text-muted)' }}>{row.listing_number}</span>{row.report_classification === 'unknown' || row.report_classification === 'conflicting' ? <span className="mt-0.5 block font-normal">{row.report_classification === 'unknown' ? 'Unknown classification' : 'Conflicting classification'}</span> : null}</td>
                      <td className="px-3 py-2">{date(row.transaction_date)}</td>
                      <td className="px-3 py-2">{row.transaction_status || '—'}</td>
                      <td className="px-3 py-2">{date(row.status_change_date) || '—'}</td>
                      <td className="px-3 py-2">{row.market_center_name}</td>
                      <td className="px-3 py-2">{row.team_names}</td>
                      <td className="px-3 py-2">{row.associate_names}</td>
                      <td className="max-w-48 px-3 py-2">{contactsIncluded ? <><span className="block truncate">{contactName(row.transfer_attorney ?? {}) || '—'}</span><span className="block truncate text-[11px]" style={{ color: 'var(--text-muted)' }}>{row.transfer_attorney_firm || '—'}</span></> : 'Restricted'}</td>
                      <td className="max-w-48 px-3 py-2">{contactsIncluded ? <><span className="block truncate">{contactName(row.bond_attorney ?? {}) || '—'}</span><span className="block truncate text-[11px]" style={{ color: 'var(--text-muted)' }}>{row.bond_attorney_firm || '—'}</span></> : 'Restricted'}</td>
                      <td className="max-w-48 px-3 py-2">{contactsIncluded ? <><span className="block truncate">{contactName(row.bond_originator ?? {}) || '—'}</span><span className="block truncate text-[11px]" style={{ color: 'var(--text-muted)' }}>{row.bond_originator_company || '—'}</span></> : 'Restricted'}</td>
                      <td className="px-3 py-2">{row.transaction_financing_type || '—'}</td>
                      <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums">{money(row.sales_price) || '—'}</td>
                    </tr>
                    {expandedId === row.transaction_id ? (
                      <tr key={`${row.transaction_id}-details`} className="border-t" style={{ borderColor: 'var(--border-soft)', background: 'var(--surface-strong)' }}>
                        <td colSpan={13} className="px-2 py-2 sm:px-3"><TransactionReportExpandedDetails row={row} contactsIncluded={contactsIncluded} /></td>
                      </tr>
                    ) : null}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
          <ReportPagination
            page={data.page}
            totalPages={data.total_pages}
            pageSize={data.page_size}
            totalItems={data.total_items}
            onPageChange={(page) => { const next = { ...filters, page }; setFilters(next); setDraft(next); }}
            onPageSizeChange={(page_size) => { const next = { ...filters, page_size, page: 1 }; setFilters(next); setDraft(next); }}
            pageSizeOptions={[25, 50, 100]}
          />
        </section>
      ) : null}
    </div>
  );
}

export default function TransactionsReport({ auth }: { auth: TransactionsReportAuth }) {
  return <TransactionsReportView auth={auth} />;
}

export function TransactionsReportPreview() {
  return (
    <TransactionsReportView
      previewMode
      auth={{ token: null, activeContext: null, isRegionalAdmin: true, isOfficeAdmin: false }}
    />
  );
}